import { useState } from 'react';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import type { LabeledStat } from '@/components/common/StatCardGrid';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getActiveStatCard, getInactiveStatCard } from '@/utils/activeStatCards';
import { marketProjectsInfiniteQueryOptions } from '../api/marketPromotionP2.api';
import { MarketProjectFormDialog } from '../components/MarketProjectFormDialog';
import { getMarketProjectColumns } from '../components/MpP2Columns';
import { useMarketProjectFilters } from '../hooks/useMarketProjectFilters';
import { useDeleteMarketProject } from '../hooks/useMpP2Mutations';
import type { MarketProject, MarketProjectResponse } from '../types/marketPromotionP2.types';

function getMarketProjectStatCards(meta: MarketProjectResponse['meta'] | undefined): LabeledStat[] {
  return [
    { key: 'projects', label: 'Projects', value: meta?.total },
    getActiveStatCard(meta?.totals),
    getInactiveStatCard(meta?.totals),
    {
      key: 'beforeDeadline',
      label: 'Before Deadline',
      value: meta?.totals.beforeDeadline,
    },
  ];
}

export function MarketProjectPage() {
  const controller = useMarketProjectFilters();
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(marketProjectsInfiniteQueryOptions(controller.search));

  const [editing, setEditing] = useState<MarketProject | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<MarketProject | null>(null);

  const remove = useDeleteMarketProject(() => setPendingDelete(null));

  const columns = getMarketProjectColumns({
    onEdit: (row) => {
      setEditing(row);
      setFormOpen(true);
    },
    onDelete: setPendingDelete,
  });

  return (
    <CatalogueListLayout
      title="Market Promotion Project"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search projects',
      }}
      filters={controller}
      stats={{
        cards: getMarketProjectStatCards(firstPageMeta),
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
      onAdd={() => {
        setEditing(null);
        setFormOpen(true);
      }}
      addLabel="Add Project"
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the projects"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No projects found"
          {...loadMoreProps}
        />
      )}

      <MarketProjectFormDialog
        key={`${editing?.id ?? 'new'}:${String(isFormOpen)}`}
        project={editing}
        open={isFormOpen}
        onOpenChange={setFormOpen}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Retire this project?"
        description={
          pendingDelete === null
            ? ''
            : `"${pendingDelete.name ?? 'This project'}" will no longer be offered.`
        }
        confirmLabel="Remove"
        confirmVariant="destructive"
        isPending={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </CatalogueListLayout>
  );
}
